import { Injectable, Optional } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse
} from '@angular/common/http';

import { Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

import { MsalService } from '@azure/msal-angular';

import { ToastMessageOptions } from 'primeng/api';
import { MessageService } from './message-service';

import { LoggingService } from './logging.service';
import { DateTime } from 'luxon';
import { AuthService } from '../authentication/auth.service';

class ServerValidationResponse {
    errors: { [key: string]: string | string[] };
    title: string;
    status: number;
    traceId: string;
}

@Injectable({
    providedIn: 'root'
})
export class ApiInterceptor implements HttpInterceptor {

    readonly _connectionErrorMsg: ToastMessageOptions = {
        severity: 'error',
        summary: 'No Connection',
        detail: 'Could not reach server. Check your internet connection and try again.'
    };

    readonly _badRequestMsg: ToastMessageOptions = {
        severity: 'warn',
        summary: 'Invalid Request',
        detail: 'Please correct the following errors:',
        sticky: true,
        closable: true
    };

    readonly _unauthorizedMsg: ToastMessageOptions = {
        severity: 'warn',
        summary: 'Unauthorized',
        detail: 'You have been logged out. Please log in again.'
    };

    readonly _forbiddenMsg: ToastMessageOptions = {
        severity: 'warn',
        summary: 'Action Not Allowed',
        detail: 'You do not have permission to perform this action.'
    };

    readonly _notFoundMsg: ToastMessageOptions = {
        severity: 'warn',
        summary: 'Missing Resource',
        detail: 'Could not find server endpoint. Please contact support.'
    };

    readonly _apiErrorMsg: ToastMessageOptions = {
        severity: 'error',
        summary: 'Server Error',
        detail: 'The server has encountered an error. If you keep getting this message, contact support.'
    };

    constructor(
        private authService: AuthService,
        @Optional() private msal: MsalService,
        private messageService: MessageService,
        private logger: LoggingService) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return this.authService.IsReady.pipe(
            filter(isReady => !!isReady),
            take(1),
            switchMap(() => {
                req = req.clone({ withCredentials: true });

                return next.handle(req).pipe(catchError((error: HttpErrorResponse) => {

                    this.logger.error('API Error', { error });

                    if (error.error instanceof ErrorEvent) {
                        this.messageService.add(this._connectionErrorMsg);
                    }
                    else if (error.status === 400) {
                        const response: ServerValidationResponse = error.error;

                        const errors: string[] = Object.values(response.errors).flat(1);

                        this.messageService.add({
                            ...this._badRequestMsg,
                            data: { list: errors }
                        });
                    }
                    else if (error.status === 401) { // unauthorized
                        this.messageService.add(this._unauthorizedMsg);

                        // slight delay so users can see the toast
                        setTimeout(() => {
                            this.msal.logoutRedirect();
                        }, 3000);
                    }
                    else if (error.status === 403) { // forbidden
                        this.messageService.add(this._forbiddenMsg);
                    }
                    else if (error.status === 404) { // not found
                        this.messageService.add(this._notFoundMsg);
                    }
                    else if (error.status >= 500 || error.status === 424) { // API error
                        const response: ServerValidationResponse = error.error;

                        const errors: string[] = Object.values(response.errors).flat(1);

                        this.messageService.add({
                            ...this._apiErrorMsg,
                            data: { list: errors }
                        });
                    }

                    return throwError(() => error);
                }));
        }));
    }
}
